import React, { useCallback, useEffect, useState } from 'react';
import lookie from 'lookie';
import { Input } from 'antd';
import { useLatest } from 'ahooks';
import { Button } from 'antd-mobile';
import { Keyboard } from '@capacitor/keyboard';
import { useDispatch, useSelector } from 'react-redux';
import { CURRENT_ANDROID_ID } from '@/constant';
import service from '@/service';
import { selectDevicePlatform, selectedStoreInfo, setStoreInfo } from '@/store/device';
import { SlideModal, Toast } from '@/components';

import cx from './index.module.scss';

interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const EditStoreName: React.FC<Props> = ({ open, setOpen }) => {
  const devicePlatform = useLatest(useSelector(selectDevicePlatform));
  const dispatch = useDispatch();
  const [storeName, setStoreName] = useState<string>(null);
  const storeInfo = useSelector(selectedStoreInfo);
  const [isConfirmEditLoading, setIsConfirmEditLoading] = useState(false);
  const [isKeyBoardShow, setIsKeyBoardShow] = useState(false);

  const getBoundStore = useCallback(async () => {
    const androidId = lookie.get(CURRENT_ANDROID_ID);
    const { data } = await service.system.getBoundStore(androidId);
    dispatch(setStoreInfo(data));
  }, [dispatch]);

  const handleUpdateStoreName = useCallback(async () => {
    try {
      setIsConfirmEditLoading(true);
      await service.system.updateStoreName({ customName: storeName });
      setOpen(false);
      await getBoundStore();
      Toast.success('修改成功');
    } catch (e) {
      Toast.fail(e?.message);
    } finally {
      setIsConfirmEditLoading(false);
    }
  }, [getBoundStore, setOpen, storeName]);

  const initListener = useCallback(() => {
    if (devicePlatform.current === 'android') {
      Keyboard.addListener('keyboardWillShow', () => {
        setIsKeyBoardShow(true);
      });
      Keyboard.addListener('keyboardWillHide', () => {
        setIsKeyBoardShow(false);
      });

      return () => {
        Keyboard.removeAllListeners();
      };
    }
  }, [devicePlatform]);

  useEffect(() => {
    return initListener();
  }, [initListener]);

  useEffect(() => {
    if (open) {
      setStoreName(storeInfo?.customName || storeInfo?.name);
    }
  }, [open, storeInfo]);

  return (
    <SlideModal open={open} contentClassName={cx('EditStoreName', { 'keyboard-open': isKeyBoardShow })}>
      <div className={cx('title')}>编辑门店名称</div>
      <div className={cx('sub-title')}>设备所属门店</div>
      <Input
        className={cx('edit-name')}
        value={storeName}
        showCount
        maxLength={20}
        onChange={event => setStoreName(event.target?.value)}
      />
      <div className={cx('operate-btns')}>
        <Button
          color="primary"
          className={cx('btn', 'cancel')}
          onClick={() => {
            setOpen(false);
          }}
        >
          取消
        </Button>
        <Button
          loading={isConfirmEditLoading}
          color="primary"
          className={cx('btn', 'confirm')}
          onClick={handleUpdateStoreName}
        >
          确认修改
        </Button>
      </div>
    </SlideModal>
  );
};

export default EditStoreName;
